import he from "he";

export const SITE_URL = "https://www.timothysykes.com/";
export const ORGANIZATION_ID = `${SITE_URL}#organization`;
export const WEBSITE_ID = `${SITE_URL}#website`;

export const ORGANIZATION_SAME_AS = [
	"https://en.wikipedia.org/wiki/Timothy_Sykes",
	"https://www.youtube.com/@TimothySykesTrader",
	"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng",
	"https://www.facebook.com/timsykesfans/",
	"https://twitter.com/timothysykes",
	"https://www.linkedin.com/in/timothysykes/",
	"https://www.instagram.com/timothysykes/",
	"https://www.tiktok.com/@timothysykes",
	"https://profit.ly/user/timothysykes"
];

export const ORGANIZATION_LOGO = {
	"@type": "ImageObject",
	url: `${SITE_URL}images/ts-logo-icon.png`,
	width: 512,
	height: 512
};

export const ORGANIZATION_IMAGE = {
	"@type": "ImageObject",
	url: `${SITE_URL}images/ts-logo-white-bg.png`,
	width: 503,
	height: 95
};

export const organizationSchema = {
	"@context": "https://schema.org",
	"@type": "Organization",
	"@id": ORGANIZATION_ID,
	name: "Timothy Sykes",
	url: SITE_URL,
	description: "How To Trade Penny Stocks Without The Stock Promoter BS",
	disambiguatingDescription:
		"A leading provider of investment education and resources, founded in 2005.",
	foundingDate: "2005-01-15",
	logo: ORGANIZATION_LOGO,
	image: ORGANIZATION_IMAGE,
	sameAs: ORGANIZATION_SAME_AS,
	contactPoint: [
		{
			"@type": "ContactPoint",
			contactType: "customer service",
			telephone: "+1-888-878-3621",
			email: "support-alerts@timothysykes.com",
			areaServed: "US",
			availableLanguage: ["English", "Spanish"]
		},
		{
			"@type": "ContactPoint",
			contactType: "media inquiries",
			telephone: "+1-800-878-3621",
			email: "press@timothysykes.com",
			areaServed: "US",
			availableLanguage: ["English"]
		}
	],
	address: {
		"@type": "PostalAddress",
		streetAddress: "66 W. Flagler St. - Suite 900",
		addressLocality: "Miami",
		addressRegion: "FL",
		postalCode: "33130",
		addressCountry: "US"
	},
	founder: {
		"@type": "Person",
		name: "Timothy Sykes",
		url: `${SITE_URL}blog/author/tim-sykes/`,
		sameAs: [
			"https://www.youtube.com/@TimothySykesTrader",
			"https://en.wikipedia.org/wiki/Timothy_Sykes",
			"https://profit.ly/user/timothysykes",
			"https://www.facebook.com/timsykesfans/",
			"https://twitter.com/timothysykes",
			"https://www.linkedin.com/in/timothysykes/",
			"https://www.instagram.com/timothysykes/",
			"https://www.tiktok.com/@timothysykes",
			"https://www.imdb.com/name/nm2418506/",
			"https://observer.com/author/timothy-sykes/",
			"https://books.apple.com/us/author/timothy-sykes/id281184034",
			"https://www.huffpost.com/author/tim-501",
			"https://fortune.com/author/timothy-sykes/",
			"https://www.entrepreneur.com/author/timothy-sykes",
			"https://www.goodreads.com/author/list/748145.Timothy_Sykes",
			"https://www.amazon.com/stores/author/B002BLQ4YC",
			"https://www.businessinsider.com/author/timothy-sykes",
			"https://www.theguardian.com/profile/timothy-sykes"
		],
		image: [
			{
				"@type": "ImageObject",
				url: "https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp",
				width: 306,
				height: 384
			},
			{
				"@type": "ImageObject",
				url: "https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
				height: 96,
				width: 96
			}
		]
	},
	award: ["Timothy Sykes Day Trading Award for the Talented"],
	publishingPrinciples: `${SITE_URL}editorial-guidelines/`
};

export const websiteSchema = {
	"@context": "https://schema.org",
	"@type": "WebSite",
	"@id": WEBSITE_ID,
	name: "Timothy Sykes",
	url: SITE_URL,
	publisher: { "@id": ORGANIZATION_ID },
	potentialAction: {
		"@type": "SearchAction",
		target: {
			"@type": "EntryPoint",
			urlTemplate: `${SITE_URL}search/?q={search_term_string}`
		},
		"query-input": {
			"@type": "PropertyValueSpecification",
			valueRequired: "http://schema.org/True",
			valueName: "search_term_string"
		}
	}
};

const EXCHANGE_TICKER_REGEX =
	/[[(]\s*(NASDAQ|NYSE|AMEX|OTCPK)\s*:\s*([A-Z][A-Z.]*)\s*[\])]?/gi;

/** Matches the news template lead-in: "..., 2026 SoundHound AI Inc. stock [NASDAQ: SOUN]" */
const LEAD_IN_COMPANY_REGEX =
	/\b\d{4}\s+([A-Z0-9][^[\]()]{1,100}?)\s+stock\s*[[(]\s*(NASDAQ|NYSE|AMEX|OTCPK)\s*:\s*([A-Z][A-Z.]*)/gi;

/**
 * Finds every exchange-qualified ticker in the article HTML, in order of
 * first appearance, with the company name when the news template provides it.
 */
export function extractReferencedCompanies(html) {
	if (!html) return [];

	const text = he.decode(String(html).replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ");

	const names = new Map();
	for (const [, name, , symbol] of text.matchAll(LEAD_IN_COMPANY_REGEX)) {
		const key = symbol.toUpperCase();
		if (!names.has(key)) names.set(key, name.trim());
	}

	const companies = new Map();
	for (const [, exchange, symbol] of text.matchAll(EXCHANGE_TICKER_REGEX)) {
		const key = symbol.toUpperCase().replace(/\.$/, "");
		if (companies.has(key)) continue;
		companies.set(key, {
			exchange: exchange.toUpperCase(),
			symbol: key,
			name: names.get(key) || null
		});
	}

	return [...companies.values()];
}

export function toCorporationNode({ exchange, symbol, name }) {
	return {
		"@type": "Corporation",
		name: name || symbol,
		tickerSymbol: `${exchange}:${symbol}`
	};
}
